import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import { LINKS } from "../lib/content";

const SECTIONS = [
  {
    label: "Problem",
    body: [
      "Enterprise buyers wanted AI capabilities — natural-language querying, automated insight generation, intelligent monitoring — without abandoning the Tableau and Power BI environments their teams had spent years standardizing on. Rip-and-replace was a non-starter; every proposal had to respect entrenched workflows, governance requirements, and the political reality that BI owners defend their stacks.",
    ],
  },
  {
    label: "Approach",
    body: [
      "Across seven strategic enterprise deals at Axion Ray, I led consultative discovery designed to surface the real constraint map before any architecture was drawn: who owns the data, where governance sits, what breaks if a workflow changes. Solution architectures then layered LLM-driven intelligence over the existing analytics stack rather than beside it — retrieval-augmented generation grounded in the client's own data, orchestrated through n8n automation, integrated into the dashboards teams already lived in. Because enterprise AI fails operationally more often than technically, each architecture shipped with fault-tolerant runbooks and automated monitoring, so failures degrade gracefully instead of eroding trust in the system.",
    ],
  },
  {
    label: "Outcome",
    body: [
      "Solution turnaround time dropped roughly 40%, workflow downtime fell 80%, and consultative discovery playbooks tripled engagement capacity across pre- and post-sales. Customer time-to-value improved ~25%.",
    ],
  },
];

const STACK = ["LLMs", "RAG", "Tableau", "Power BI", "n8n", "Python"];

const HeroSvg = () => (
  <svg viewBox="0 0 800 320" xmlns="http://www.w3.org/2000/svg" role="img"
    aria-label="Layered architecture: existing BI stack with LLM + RAG intelligence layer above and a monitoring band"
    style={{ width: "100%", height: "auto", display: "block" }}>
    <defs>
      <linearGradient id="ea-band" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#8B5CF6" stopOpacity="0.85" />
        <stop offset="1" stopColor="#3B82F6" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="ea-band-2" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#3B82F6" stopOpacity="0.55" />
        <stop offset="1" stopColor="#8B5CF6" stopOpacity="0.55" />
      </linearGradient>
      <linearGradient id="ea-mon" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#475569" stopOpacity="0.35" />
        <stop offset="1" stopColor="#475569" stopOpacity="0.15" />
      </linearGradient>
    </defs>
    {/* Monitoring band */}
    <rect x="30" y="30" width="740" height="34" rx="10" fill="url(#ea-mon)" stroke="#475569" strokeOpacity="0.35" />
    <text x="50" y="52" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#94A3B8">automated monitoring · fault-tolerant runbooks</text>
    {/* LLM + RAG intelligence layer */}
    <rect x="30" y="82" width="740" height="80" rx="14" fill="url(#ea-band)" />
    <text x="50" y="110" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff" opacity="0.9">INTELLIGENCE LAYER · LLM + RAG</text>
    <g fontFamily="ui-sans-serif, system-ui" fontSize="13" fill="#fff">
      <text x="50" y="140">natural-language querying</text>
      <text x="290" y="140">automated insight generation</text>
      <text x="560" y="140">n8n orchestration</text>
    </g>
    {/* Existing BI stack */}
    <rect x="30" y="180" width="740" height="110" rx="14" fill="url(#ea-band-2)" opacity="0.35" stroke="#3B82F6" strokeOpacity="0.5" />
    <text x="50" y="208" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#3B82F6">EXISTING BI STACK</text>
    {[
      { x: 60, y: 225, l: "Tableau" },
      { x: 220, y: 225, l: "Power BI" },
      { x: 400, y: 225, l: "governance" },
      { x: 590, y: 225, l: "existing workflows" },
    ].map((c) => (
      <g key={c.l}>
        <rect x={c.x} y={c.y} width="140" height="48" rx="8" fill="#0B1120" fillOpacity="0.55" stroke="#3B82F6" strokeOpacity="0.35" />
        <text x={c.x + 12} y={c.y + 30} fontFamily="ui-sans-serif, system-ui" fontSize="13" fill="#E2E8F0">{c.l}</text>
      </g>
    ))}
    {/* Connectors between layers */}
    {[100, 300, 500, 700].map((x) => (
      <line key={x} x1={x} y1="162" x2={x} y2="180" stroke="#8B5CF6" strokeOpacity="0.6" strokeDasharray="3 3" />
    ))}
  </svg>
);

export default function EnterpriseAiAnalytics() {
  useEffect(() => { document.title = "AI-Powered Solution Architecture for Enterprise Analytics - Yash Bhatt"; }, []);
  return (
    <main data-testid="enterprise-ai-analytics-page" className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/projects" className="hover:text-secondary transition-colors duration-200">Projects</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary">Enterprise Analytics</li>
            </ol>
          </nav>
          <MaskedLine>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-5">Solution Architecture · Axion Ray</p>
          </MaskedLine>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.15] pb-2">
            <MaskedLine delay={0.15}>AI-Powered Solution Architecture for <span className="text-gradient">Enterprise Analytics</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-6 font-display text-lg text-gradient font-medium">~40% faster solution turnaround · 80% less workflow downtime · 3× engagement capacity</p>
          </MaskedLine>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <Reveal className="mb-14">
          <figure className="rounded-2xl border border-border bg-card/40 p-5 md:p-8" data-testid="case-hero-svg">
            <HeroSvg />
            <figcaption className="mt-4 text-xs font-mono text-muted-foreground">Intelligence layered over the existing analytics stack — never beside it.</figcaption>
          </figure>
        </Reveal>

        {SECTIONS.map((s, si) => (
          <Reveal key={s.label} delay={si * 0.05} className="mb-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-5">{s.label}</p>
            {s.body.map((para, pi) => (
              <p key={pi} className="text-base md:text-lg text-foreground/85 leading-relaxed mb-5">{para}</p>
            ))}
          </Reveal>
        ))}

        <Reveal className="mt-12 flex flex-wrap gap-2">
          {STACK.map((t) => (
            <span key={t} className="px-3 py-1 rounded-full border border-border text-xs text-muted-foreground">{t}</span>
          ))}
        </Reveal>

        <Reveal className="mt-16 pt-10 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <Link to="/projects" data-testid="case-back-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <a href={LINKS.calendly} target="_blank" rel="noreferrer" data-testid="case-cta"
            className="group inline-flex h-12 items-center gap-2 px-7 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
            Book a Consultation <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </article>
    </main>
  );
}
